import { ReactElement, ComponentType } from 'react';
import { Message, useMessageGetter } from '@messageformat/react';
import { IconSend, IconConfirm, IconBuy, IconTime } from '@op/opds.opux-icons/action';
import { IconMore } from '@op/opds.opux-icons/navigation';
import { IconInvestments } from '@op/opds.opux-icons/business';
import { space } from '@op/opds.tokens';
import TimeSpanSection from './TimeSpanSection';
import TimeSpanCell from './TimeSpanCell';
import styles from './TimeSpan.module.scss';

type TimeSpanKey = '1' | '2';

interface TimeSpanProps {
  timeSpanKey: TimeSpanKey;
}

interface CellConfig {
  titleKey?: string;
  icon: ComponentType<{ onDarkBg?: boolean }>;
  start?: boolean;
  end?: boolean;
  textKey: string;
}

interface SectionConfig {
  headerMessageId: string;
  cells: CellConfig[];
  addMarginTop?: boolean;
}

interface TimeSpanConfig {
  titleKey: string;
  subtitleKey: string;
  sections: SectionConfig[];
}

const TimeSpanIntro = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}): ReactElement => (
  <div className={styles.intro}>
    <h4 className={styles.title}>{title}</h4>
    <p className="investoracademy-text-paragraph">{subtitle}</p>
  </div>
);

const timeSpanConfigs: Record<TimeSpanKey, TimeSpanConfig> = {
  '1': {
    titleKey: 'title1',
    subtitleKey: 'subtitle1',
    sections: [
      {
        headerMessageId: 'course6.timespansection.sectionHeader1',
        cells: [
          {
            titleKey: 'cellTitle1',
            icon: IconSend,
            start: true,
            textKey: 'sectionText1',
          },
          { titleKey: 'cellTitle2', icon: IconMore, end: true, textKey: 'sectionText2' },
        ],
      },
      {
        headerMessageId: 'course6.timespansection.sectionHeader2',
        addMarginTop: true,
        cells: [
          {
            titleKey: 'cellTitle3',
            icon: IconSend,
            start: true,
            textKey: 'sectionText1',
          },
          { titleKey: 'cellTitle2', icon: IconBuy, textKey: 'sectionText3' },
          {
            titleKey: 'cellTitle4',
            icon: IconInvestments,
            end: true,
            textKey: 'sectionText2',
          },
        ],
      },
    ],
  },
  '2': {
    titleKey: 'title2',
    subtitleKey: 'subtitle2',
    sections: [
      {
        headerMessageId: 'course6.timespansection.sectionHeader3',
        cells: [
          {
            titleKey: 'cellTitle5',
            icon: IconSend,
            start: true,
            textKey: 'sectionText1',
          },
          { icon: IconConfirm, textKey: 'sectionText4' },
          { icon: IconBuy, textKey: 'sectionText5' },
          { titleKey: 'cellTitle2', icon: IconTime, textKey: 'sectionText6' },
          {
            titleKey: 'cellTitle4',
            icon: IconInvestments,
            end: true,
            textKey: 'sectionText2',
          },
        ],
      },
      {
        headerMessageId: 'course6.timespansection.sectionHeader4',
        addMarginTop: true,
        cells: [
          {
            titleKey: 'cellTitle5',
            icon: IconSend,
            start: true,
            textKey: 'sectionText1',
          },
          { icon: IconConfirm, textKey: 'sectionText4' },
          { icon: IconBuy, textKey: 'sectionText5' },
          {
            titleKey: 'cellTitle2',
            icon: IconInvestments,
            end: true,
            textKey: 'sectionText2',
          },
        ],
      },
    ],
  },
};

const TimeSpan = ({ timeSpanKey }: TimeSpanProps): ReactElement => {
  const getSpanTexts = useMessageGetter('course6.timespansection');
  const config = timeSpanConfigs[timeSpanKey];

  return (
    <>
      <TimeSpanIntro
        title={getSpanTexts(config.titleKey)}
        subtitle={getSpanTexts(config.subtitleKey)}
      />
      {config.sections.map((section) => (
        <div key={section.headerMessageId}>
          <h5
            className={styles.subtitle}
            style={section.addMarginTop ? { marginTop: space.xxxl } : undefined}
          >
            <Message id={section.headerMessageId} />
          </h5>
          <TimeSpanSection>
            {section.cells.map((cell, index) => (
              <TimeSpanCell
                key={`${section.headerMessageId}-${cell.textKey}`}
                title={cell.titleKey ? getSpanTexts(cell.titleKey) : undefined}
                icon={cell.icon}
                start={cell.start}
                end={cell.end}
                isFirst={index === 0}
                isLast={index === section.cells.length - 1}
              >
                {getSpanTexts(cell.textKey)}
              </TimeSpanCell>
            ))}
          </TimeSpanSection>
        </div>
      ))}
    </>
  );
};

export default TimeSpan;
